import { formatFullDate } from '@schuelerhilfe/shared/src/utils/dayjs';
import { useTransition } from 'react';
import type { Dispatch } from 'react';
import type { BookingAction } from './bookingReducer';
import { submitForm } from '@/lib/api';
import { sourceUrlWithCampaign } from '@/lib/form/sourceUrl';
import type { ContactFormSubmitData } from '@/lib/form/types';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';

export function useBookingSubmit(
  dispatch: Dispatch<BookingAction>,
  locationSlug: string | undefined,
  submitTracking: EventTrackingResolved[] | undefined,
  successTracking: EventTrackingResolved[] | undefined,
  /**
   * Postcode of the Standort the visitor picked in the widget's location
   * step; sent as `plz` next to `locationSlug`. Undefined on Location Pages,
   * whose payload stays as before.
   */
  postcode?: string,
) {
  const [isPending, startTransition] = useTransition();

  const handleSubmit = (
    submission: ContactFormSubmitData,
    selectedDay: string,
    selectedTime: string,
  ): Promise<void> =>
    new Promise((resolve) => {
      pushCmsEventTracking(submitTracking);
      startTransition(async () => {
        try {
          await submitForm({
            type: 'appointment',
            salutation: submission.salutation,
            firstname: submission.firstname,
            lastname: submission.lastname,
            email: submission.email,
            phone: submission.phone,
            desiredDate: `${selectedDay} ${selectedTime}`.trim(),
            locationSlug: locationSlug ?? '',
            sourceUrl: sourceUrlWithCampaign(),
            customerNumber: submission.customerNumber,
            ...(postcode ? { plz: postcode } : {}),
          });
          pushCmsEventTracking(successTracking);
        } catch {
          // always show success — customer service handles failed bookings —
          // but only track the GA4 conversion when the request actually succeeded
        }
        dispatch({
          type: 'SUBMIT_SUCCESS',
          snapshot: {
            salutation: submission.salutation,
            lastname: submission.lastname,
            date: formatFullDate(selectedDay),
            time: selectedTime,
          },
        });
        resolve();
      });
    });

  return { handleSubmit, isPending };
}
